import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from './Dialog';
import { Button } from './Button';

/**
 * ColorPickerModal — canonical color-picker for the app.
 *
 * Iter18 Polish Round 2 §1.1 · matches SET-2 branding wire spec:
 *   1. "Pick a shade" — HSV saturation/value crosshair area (draggable pin)
 *   2. "Hue" — 14px hue slider
 *   3. HEX input (left) + 6 preset chips (right)
 *   4. Footer: Cancel (secondary) · Apply (primary red)
 *
 * The 6 presets are the wire's brand-consistent seed set. If a project needs
 * a different preset row, pass `presets` prop.
 *
 * Props:
 *   open        boolean
 *   onClose     () => void
 *   onApply     (hex: string) => void   fires with the selected color on Apply
 *   value       string (hex, e.g. "#D70321") — the starting color
 *   title       string — modal header (e.g. "Header color")
 *   presets     string[] — optional; defaults to the 6-swatch brand row
 */

const DEFAULT_PRESETS = ['#1F1F1F', '#4A4A4A', '#7A7A7A', '#ABABAB', '#D70321', '#FFFFFF'];

// ────────────────────────────────────────────────────────────────────────────
// HSV ↔ HEX helpers
// ────────────────────────────────────────────────────────────────────────────

function hexToRgb(hex) {
  const clean = hex.replace('#', '').padStart(6, '0').slice(0, 6);
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const to2 = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`.toUpperCase();
}

function rgbToHsv({ r, g, b }) {
  const rr = r / 255, gg = g / 255, bb = b / 255;
  const max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rr) h = ((gg - bb) / d) % 6;
    else if (max === gg) h = (bb - rr) / d + 2;
    else h = (rr - gg) / d + 4;
    h = h * 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  const v = max;
  return { h, s, v };
}

function hsvToRgb({ h, s, v }) {
  const c = v * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let rgb = [0, 0, 0];
  if (0 <= hp && hp < 1) rgb = [c, x, 0];
  else if (1 <= hp && hp < 2) rgb = [x, c, 0];
  else if (2 <= hp && hp < 3) rgb = [0, c, x];
  else if (3 <= hp && hp < 4) rgb = [0, x, c];
  else if (4 <= hp && hp < 5) rgb = [x, 0, c];
  else if (5 <= hp && hp < 6) rgb = [c, 0, x];
  const m = v - c;
  return { r: (rgb[0] + m) * 255, g: (rgb[1] + m) * 255, b: (rgb[2] + m) * 255 };
}

function hexToHsv(hex) {
  return rgbToHsv(hexToRgb(hex));
}

function hsvToHex(hsv) {
  return rgbToHex(hsvToRgb(hsv));
}

function normalizeHex(input) {
  const raw = String(input || '').trim().replace(/^#/, '').toUpperCase();
  if (!/^[0-9A-F]{3}([0-9A-F]{3})?$/.test(raw)) return null;
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  return `#${full}`;
}

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

export default function ColorPickerModal({
  open,
  onClose,
  onApply,
  value,
  title = 'Pick a color',
  presets = DEFAULT_PRESETS,
}) {
  // Store working state as HSV so hue slider + spectrum stay stable across renders.
  const [hsv, setHsv] = useState(() => hexToHsv(value || '#D70321'));
  const [hexInput, setHexInput] = useState(value || '#D70321');

  // Re-sync when the modal reopens against a new starting color.
  useEffect(() => {
    if (open) {
      const start = normalizeHex(value) || '#D70321';
      setHsv(hexToHsv(start));
      setHexInput(start);
    }
  }, [open, value]);

  const currentHex = useMemo(() => hsvToHex(hsv), [hsv]);

  // Keep the hex input in sync as the user drags the spectrum / hue.
  useEffect(() => {
    setHexInput(currentHex);
  }, [currentHex]);

  const handleHexChange = (raw) => {
    setHexInput(raw);
    const normalized = normalizeHex(raw);
    if (normalized) setHsv(hexToHsv(normalized));
  };

  const handleApply = () => {
    const finalHex = normalizeHex(hexInput) || currentHex;
    onApply?.(finalHex);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} size="sm">
      <DialogHeader onClose={onClose}>{title}</DialogHeader>
      <DialogBody>
        <div className="space-y-4">
          {/* 1. Pick a shade — HSV saturation/value spectrum */}
          <div>
            <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-muted)' }}>
              Pick a shade
            </p>
            <HSVSpectrum
              hue={hsv.h}
              saturation={hsv.s}
              value={hsv.v}
              onChange={(s, v) => setHsv((prev) => ({ ...prev, s, v }))}
            />
          </div>

          {/* 2. Hue slider */}
          <div>
            <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-muted)' }}>
              Hue
            </p>
            <HueSlider
              value={hsv.h}
              onChange={(h) => setHsv((prev) => ({ ...prev, h }))}
            />
          </div>

          {/* 3. Hex input + preset chips */}
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label
                htmlFor="cp-hex"
                className="block text-xs font-medium mb-1"
                style={{ color: 'var(--color-text-muted)' }}
              >
                HEX
              </label>
              <div className="flex items-center gap-2">
                <span
                  className="h-6 w-6 rounded flex-shrink-0"
                  style={{
                    backgroundColor: currentHex,
                    border: '1px solid var(--color-border)',
                  }}
                  aria-hidden="true"
                />
                <input
                  id="cp-hex"
                  type="text"
                  value={hexInput}
                  onChange={(e) => handleHexChange(e.target.value)}
                  className="input flex-1 uppercase"
                  maxLength={7}
                  aria-label="Hex color value"
                />
              </div>
            </div>
            <div>
              <p
                className="block text-xs font-medium mb-1"
                style={{ color: 'var(--color-text-muted)' }}
              >
                Presets
              </p>
              <div className="flex gap-1.5">
                {presets.map((swatch) => (
                  <button
                    key={swatch}
                    type="button"
                    onClick={() => {
                      setHsv(hexToHsv(swatch));
                      setHexInput(swatch);
                    }}
                    className="h-6 w-6 rounded-full transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                    style={{
                      backgroundColor: swatch,
                      border: currentHex.toUpperCase() === swatch.toUpperCase()
                        ? '2px solid var(--color-text-default)'
                        : '1px solid var(--color-border)',
                    }}
                    aria-label={`Set color to ${swatch}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply}>Apply</Button>
      </DialogFooter>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// HSV spectrum (saturation × value plane)
// ────────────────────────────────────────────────────────────────────────────

function HSVSpectrum({ hue, saturation, value, onChange }) {
  const boxRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const commit = useCallback(
    (clientX, clientY) => {
      const el = boxRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
      onChange(x, 1 - y);
    },
    [onChange]
  );

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const p = e.touches ? e.touches[0] : e;
      commit(p.clientX, p.clientY);
    };
    const stop = () => setDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchmove', handleMove, { passive: true });
    window.addEventListener('touchend', stop);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', stop);
    };
  }, [dragging, commit]);

  const pinX = `${saturation * 100}%`;
  const pinY = `${(1 - value) * 100}%`;
  const hueRgb = hsvToRgb({ h: hue, s: 1, v: 1 });
  const hueHex = rgbToHex(hueRgb);

  return (
    <div
      ref={boxRef}
      onMouseDown={(e) => {
        setDragging(true);
        commit(e.clientX, e.clientY);
      }}
      onTouchStart={(e) => {
        setDragging(true);
        const p = e.touches[0];
        commit(p.clientX, p.clientY);
      }}
      className="relative w-full cursor-crosshair select-none"
      style={{
        height: 130,
        borderRadius: 'var(--radius-md)',
        background: `linear-gradient(to top, #000 0%, transparent 100%),
                     linear-gradient(to right, #fff 0%, ${hueHex} 100%)`,
        border: '1px solid var(--color-border)',
      }}
      role="slider"
      aria-label="Saturation and value"
      aria-valuenow={Math.round(saturation * 100)}
    >
      <div
        style={{
          position: 'absolute',
          left: pinX,
          top: pinY,
          width: 14,
          height: 14,
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          border: '2px solid #fff',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Hue slider (14px tall)
// ────────────────────────────────────────────────────────────────────────────

function HueSlider({ value, onChange }) {
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const commit = useCallback(
    (clientX) => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      onChange(x * 360);
    },
    [onChange]
  );

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const p = e.touches ? e.touches[0] : e;
      commit(p.clientX);
    };
    const stop = () => setDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchmove', handleMove, { passive: true });
    window.addEventListener('touchend', stop);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', stop);
    };
  }, [dragging, commit]);

  const pinLeft = `${(value / 360) * 100}%`;

  return (
    <div
      ref={trackRef}
      onMouseDown={(e) => {
        setDragging(true);
        commit(e.clientX);
      }}
      onTouchStart={(e) => {
        setDragging(true);
        commit(e.touches[0].clientX);
      }}
      className="relative w-full cursor-pointer select-none"
      style={{
        height: 14,
        borderRadius: 999,
        background:
          'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
        border: '1px solid var(--color-border)',
      }}
      role="slider"
      aria-label="Hue"
      aria-valuenow={Math.round(value)}
    >
      <div
        style={{
          position: 'absolute',
          left: pinLeft,
          top: '50%',
          width: 12,
          height: 18,
          transform: 'translate(-50%, -50%)',
          borderRadius: 3,
          background: '#fff',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.15)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
